import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import type { WrappedResponse } from '../../api/types';
import { canShareFiles, renderStubPng, shareOrSave } from './exportStub';
import type { Line } from '../lines';
import { Wordmark } from '../Wordmark';
import { shareQuery, stubFacts, type NextPick, type StubFacts } from './stub';

interface Props {
  data: WrappedResponse;
  /** The line this ticket is for: the first on the rider's network. */
  line: Line | null;
  /** Every line the rider rides, in order, for the route strip. */
  lines: Line[];
  /** The first ticket waiting on that line. */
  next: NextPick | null;
  /** Where the primary button leads: that line, or the hub. */
  destination: string;
  onEnter: () => void;
  onHub: (() => void) | null;
}

type Saving = 'idle' | 'working' | 'done' | 'failed';

/** Opens the post in a new tab. */
function newTab(url: URL | string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function XLogo() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      />
    </svg>
  );
}

/** The post that goes with a shared ticket. */
function shareText(f: StubFacts): string {
  const bits = [`${f.titles} titles`, `${f.hours} hours`, f.grader?.toLowerCase()].filter(Boolean);
  return `My Animetro ticket: ${bits.join(', ')}. What does yours say?`;
}

/** Smallest the ticket shrinks to before the panel is left to scroll instead. */
const MIN_ZOOM = 0.62;

/**
 * Shrinks the ticket until the stop fits its panel, so the buttons are never
 * below the fold. Only the ticket scales: zooming it shortens the panel's
 * overflow by the same share of its height.
 */
function useFitTicket() {
  const wrap = useRef<HTMLElement>(null);
  const fit = () => {
    const el = wrap.current;
    const panel = el?.parentElement;
    const lift = el?.querySelector<HTMLElement>('.stub-lift');
    if (!el || !panel || !lift) return;
    el.style.setProperty('--z', '1');
    const h = lift.offsetHeight;
    let z = 1;
    // The ticket's margin scales with it, so one pass can land a few pixels
    // out; a second look settles it.
    for (let pass = 0; pass < 3; pass++) {
      const over = panel.scrollHeight - panel.clientHeight;
      if (over <= 0 || z === MIN_ZOOM) return;
      z = Math.max(MIN_ZOOM, Math.floor((z - (over + 2) / h) * 1000) / 1000);
      el.style.setProperty('--z', String(z));
    }
  };
  useLayoutEffect(fit, []);
  useEffect(() => {
    let pending: number | undefined;
    const later = () => {
      window.clearTimeout(pending);
      pending = window.setTimeout(fit, 90);
    };
    window.addEventListener('resize', later);
    if (document.fonts?.ready) void document.fonts.ready.then(fit);
    return () => {
      window.removeEventListener('resize', later);
      window.clearTimeout(pending);
    };
  }, []);
  return wrap;
}

/** The 乗車 stamp, dated, as the PNG inks it. */
function Stamp({ issued }: { issued: string }) {
  const id = useId();
  return (
    <svg className="stamp" viewBox="-50 -50 100 100" aria-hidden="true">
      <defs>
        <path id={`${id}t`} d="M-35,0A35,35 0 0 1 35,0" />
        <path id={`${id}b`} d="M-35,0A35,35 0 0 0 35,0" />
      </defs>
      <circle r={44} className="ring" />
      <circle r={28} className="ring thin" />
      <text className="arc">
        <textPath href={`#${id}t`} startOffset="50%">
          ANIMETRO
        </textPath>
      </text>
      <text className="arc" dy="6">
        <textPath href={`#${id}b`} startOffset="50%">
          {issued}
        </textPath>
      </text>
      <text className="kj" y={8}>
        乗車
      </text>
    </svg>
  );
}

/**
 * The last stop: the profile as a printed stub, and the way to take it with you.
 * The ticket on screen and the PNG are both built from `stubFacts`, so what a
 * rider shares is what they were looking at.
 */
/**
 * Text on a button filled with the line's colour: dark, unless the colour is
 * itself too dark for that (the Dread line's red), where white reads better.
 */
function textOn(hex: string): string {
  const lin = (i: number) => {
    const c = Number.parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(1) + 0.7152 * lin(3) + 0.0722 * lin(5) < 0.2 ? '#FFFFFF' : '#14161A';
}

export function StubTicket({ data, line, lines, next, destination, onEnter, onHub }: Props) {
  const facts = stubFacts(data, line, lines, next?.title ?? null);
  const [saving, setSaving] = useState<Saving>('idle');
  const filename = `animetro-ticket-${facts.rider.toLowerCase()}.png`;
  const accent = facts.line?.color;
  const count = facts.network.length;
  const wrap = useFitTicket();

  const save = async (text?: string) => {
    setSaving('working');
    try {
      const blob = await renderStubPng(facts);
      await shareOrSave(blob, filename, text);
      setSaving('done');
    } catch {
      setSaving('failed');
    }
  };

  // On a phone this opens the share sheet with the PNG attached, and the rider
  // picks X there. On a computer X's composer opens in a new tab, prefilled
  // with a link to /api/share, whose card tags make X show the ticket under
  // the post (drawn by the backend, without the username).
  const shareX = () => {
    if (canShareFiles()) {
      void save(shareText(facts));
      return;
    }
    const url = new URL('https://x.com/intent/tweet');
    url.searchParams.set('text', shareText(facts));
    url.searchParams.set('url', `${window.location.origin}/api/share?${shareQuery(data, facts, lines, next?.id ?? null)}`);
    newTab(url);
  };

  return (
    <section
      ref={wrap}
      className="stub-wrap"
      style={
        facts.line
          ? ({
              ['--c' as string]: accent,
              ['--c-ink' as string]: facts.line.ink,
              ['--c-on' as string]: textOn(facts.line.color),
            } as CSSProperties)
          : undefined
      }
    >
      <div className="slot st" style={{ ['--d' as string]: '300ms' }} aria-hidden="true" />
      {/* the shadow sits on its own layer, since the stub's perforated edge is a mask */}
      <div className="stub-lift">
        <article className="stub" aria-label={`Ticket for ${facts.rider}`}>
          <span className="stub-wm" aria-hidden="true">
            {facts.line?.kanji.slice(0, 1) ?? '次'}
          </span>
          <header>
            <span>
              <Wordmark kana /> ・ <Wordmark />
            </span>
            <span>乗車券 ・ RIDER PASS</span>
          </header>
          <div className="stub-line">
            <b className="code">{facts.line?.code ?? 'ANI'}</b>
            <span className="nm">
              <em>{facts.line?.kanji ?? '次の一本'}</em>
              <small>{facts.line?.lineEn ?? 'ANIMETRO'}</small>
            </span>
          </div>
          <div className="who">
            <small>RIDER ・ {facts.source}</small>
            <h2>{facts.rider}</h2>
          </div>
          <Stamp issued={facts.issued} />

          {count > 0 && (
            <div className="route">
              <small>路線 ・ {count === 1 ? '1 LINE' : `${count} LINES`}</small>
              <ol style={{ ['--n' as string]: count }}>
                {facts.network.map((l, i) => (
                  <li key={l.code} style={{ ['--lc' as string]: l.color }} className={i === 0 ? 'first' : undefined}>
                    <i />
                    <span>{l.code}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <ul className="grid">
            <li>
              <b>HOME STATION</b>
              <span>{facts.genre ? `${facts.genre.toUpperCase()}${facts.genreShare ? ` ・ ${facts.genreShare}` : ''}` : '-'}</span>
            </li>
            <li>
              <b>OBSCURITY</b>
              <span>{facts.obscurity ?? '-'}</span>
            </li>
            <li>
              <b>SCORING</b>
              <span>{facts.grader ?? '-'}</span>
            </li>
            <li>
              <b>RIDDEN</b>
              <span>
                {facts.titles} TITLES ・ {facts.hours} HRS
              </span>
            </li>
            <li>
              <b>HIDDEN GEM</b>
              <span>{facts.gem ?? '-'}</span>
            </li>
            <li>
              <b>LEFT EARLY</b>
              <span>{facts.dropRate ? `${facts.dropRate} OF TITLES` : '-'}</span>
            </li>
          </ul>
          <div className="tear" />
          <footer>
            <div className="stub-next">
              <small>次の一本 ・ NEXT STOP</small>
              <span className={facts.next ? 'aboard' : undefined}>{facts.next ?? 'THE CENTRAL STATION'}</span>
            </div>
            <div className="bars" aria-hidden="true">
              {facts.bars.map((n, i) => (
                <i key={i} style={{ width: n * 2, opacity: i % 2 ? 0 : 1 }} />
              ))}
            </div>
            <div className="seat">
              <span>No. {facts.serial}</span>
              <small>ISSUED {facts.issued}</small>
            </div>
          </footer>
        </article>
      </div>

      <div className="stub-acts st" style={{ ['--d' as string]: '1500ms' }}>
        <button className="act buy" onClick={onEnter}>
          {destination}{' '}▶
        </button>
        <button className="act skip" onClick={() => void save()} disabled={saving === 'working'}>
          {saving === 'working'
            ? 'PRINTING…'
            : saving === 'done'
              ? 'SAVED ・ AGAIN?'
              : saving === 'failed'
                ? 'COULD NOT SAVE ・ RETRY'
                : '保存 ・ SAVE AS PNG'}
        </button>
        <div className="stub-more">
          <span className="share" role="group" aria-label="Share your ticket">
            SHARE ON
            <button onClick={shareX} disabled={saving === 'working'} aria-label="Share on X" title="X">
              <XLogo />
            </button>
          </span>
          {onHub && (
            <button className="linkish" onClick={onHub}>
              中央駅 ・ CENTRAL STATION
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
